// Account center calls on the existing /api/auth/* and /api/me routes (same users, same sessions).
// Contract: platform/docs/api-inventory/auth-account.md. Errors are {error:"CODE", message}.
import type { Call } from './call'

/** Terms and privacy version the web client submits at signup and WeChat login. */
export const LEGAL_VERSION = '2026-08-26'

export interface AccountProfile {
  id: string
  kind?: string
  /** Empty for WeChat-only accounts. */
  email?: string | null
  displayName?: string
  emailVerified?: boolean
  avatar?: string
  marketingSubscribed?: boolean
  isAdmin?: boolean
}

export interface SessionEntry {
  sid: string
  current: boolean
  kind: string
  ip: string
  userAgent: string
  /** Milliseconds since epoch. */
  issuedAt: number
  expiresAt: number
}

export interface SignupInput {
  email: string
  code: string
  password: string
  displayName?: string
  avatar?: string
}

export function createAccountApi(call: Call) {
  return {
    /** Profile of the current session (guests only carry id and kind). */
    getMe: () => call<{ user: AccountProfile }>({ method: 'GET', path: '/api/me' }),

    // Email signup: request a code, then verify it together with the account details.
    requestSignupCode: (email: string) =>
      call<{ ok: true; expiresIn: number }>({ method: 'POST', path: '/api/auth/email/request', body: { email } }),
    signup: (input: SignupInput) =>
      call<{ ok: true; user: AccountProfile }>({
        method: 'POST',
        path: '/api/auth/email/verify',
        body: { ...input, acceptLegal: true, termsVersion: LEGAL_VERSION, privacyVersion: LEGAL_VERSION },
      }),

    // Password reset by email code. Reset does not log the user in.
    requestPasswordReset: (email: string) =>
      call<{ ok: true; accepted: true; expiresIn: number }>({ method: 'POST', path: '/api/auth/password/reset-request', body: { email } }),
    resetPassword: (email: string, code: string, password: string, passwordConfirm: string) =>
      call<{ ok: true }>({ method: 'POST', path: '/api/auth/password/reset', body: { email, code, password, passwordConfirm } }),
    changePassword: (currentPassword: string, newPassword: string) =>
      call<{ ok: true }>({ method: 'POST', path: '/api/auth/password/change', body: { currentPassword, newPassword } }),

    updateProfile: (input: { displayName?: string; avatar?: string }) =>
      call<{ user: AccountProfile }>({ method: 'PATCH', path: '/api/me', body: input }),
    setMarketingConsent: (subscribed: boolean) =>
      call<{ user: AccountProfile }>({ method: 'POST', path: '/api/me/marketing-consent', body: { subscribed } }),

    // Sessions.
    listSessions: () => call<{ sessions: SessionEntry[] }>({ method: 'GET', path: '/api/auth/sessions' }),
    revokeSession: (sid: string) =>
      call<{ ok: true }>({ method: 'DELETE', path: `/api/auth/sessions/${encodeURIComponent(sid)}` }),
    /** Signs out every other device; the current session stays. */
    logoutAll: () => call<{ ok: true }>({ method: 'POST', path: '/api/auth/logout-all', body: {} }),

    /**
     * Permanent deletion. Password accounts send currentPassword. Accounts without a password
     * get 401 REAUTHENTICATION_REQUIRED unless they logged in within the last 10 minutes.
     */
    deleteAccount: (currentPassword?: string) =>
      call<{ ok: true }>({
        method: 'DELETE',
        path: '/api/me',
        body: currentPassword === undefined ? { confirm: true } : { confirm: true, currentPassword },
      }),

    // WeChat web QR login. The authorize URL redirects to the fixed callback page
    // (WECHAT_WEB_LOGIN_REDIRECT_URI, default https://daoyouxueba.com/wechat-login.html).
    wechatWebStart: () => call<{ ok: true; authorizeUrl: string; expiresIn: number }>({ method: 'GET', path: '/api/auth/wechat/web/start' }),
    wechatWebVerify: (code: string, state: string) =>
      call<{ ok: true; user: AccountProfile; provider: 'wechat-web' }>({
        method: 'POST',
        path: '/api/auth/wechat/web/verify',
        body: { code, state, acceptLegal: true, termsVersion: LEGAL_VERSION, privacyVersion: LEGAL_VERSION },
      }),
  }
}
